/*
  Roots wishlist
  ==============

  One store, three readers: the save mark on a product card and on the product
  page, the count in the header, and the saved-items page.

  WHY THERE IS NO ACCOUNT BEHIND THIS. Saved items live in this browser's
  localStorage and deliberately do not sync across devices or into a customer
  account. Writing to a customer metafield needs either a private app or the
  Customer Account API, and neither is a theme change. That is a real limit and
  the saved page says so in plain words rather than letting someone lose a list
  they assumed was on their account.

  WHAT IS STORED, AND WHY IT CHANGED. Entries are now `{ id, handle, title }`
  rather than a bare product id. The id alone is unusable from the client: there
  is no storefront route that turns an id back into a product, so a page built
  from ids could never render anything. The handle is what `/products/<handle>`
  needs, and it is what makes the saved page possible at all.

  Legacy bare-id entries from the earlier version are dropped on read, once,
  rather than kept as rows that could never be displayed.
*/

const KEY = 'roots:wishlist';
const CHANGE_EVENT = 'roots:wishlist:change';

function read() {
  let raw;
  try {
    raw = JSON.parse(localStorage.getItem(KEY));
  } catch (error) {
    return [];
  }
  if (!Array.isArray(raw)) return [];

  // Keep only entries that can actually be rendered. A string or number here is
  // from the id-only version and has no route we could fetch.
  return raw.filter((entry) => entry && typeof entry === 'object' && entry.handle);
}

function write(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch (error) {
    // Private-mode quota failures must not take the page down with them; the
    // toggle simply does not persist, and the in-page state still updates.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
}

const RootsWishlist = {
  KEY,
  CHANGE_EVENT,
  all: read,
  count: () => read().length,
  has: (handle) => read().some((entry) => entry.handle === handle),

  toggle(entry) {
    const items = read();
    const index = items.findIndex((item) => item.handle === entry.handle);
    if (index > -1) {
      items.splice(index, 1);
    } else {
      items.push(entry);
    }
    write(items);
    return index === -1;
  },

  remove(handle) {
    write(read().filter((entry) => entry.handle !== handle));
  },

  /*
    Both signals are needed and they do different jobs. The custom event covers
    changes made in THIS document; `storage` fires only in OTHER tabs, so a
    second tab's header count and saved page stay honest without polling.
  */
  subscribe(callback) {
    document.addEventListener(CHANGE_EVENT, () => callback(read()));
    window.addEventListener('storage', (event) => {
      if (event.key === KEY) callback(read());
    });
  }
};

window.RootsWishlist = RootsWishlist;

/* --- the save mark ------------------------------------------------------- */

class RootsSaveButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-save-toggle]');
    if (!this.button) return;

    this.label = this.querySelector('.roots-save__label');
    this.entry = {
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle || ''
    };
    this.saveText = this.dataset.saveText || 'Save';
    this.savedText = this.dataset.savedText || 'Saved';

    this.onClick = this.onClick.bind(this);
    this.sync = this.sync.bind(this);
    this.button.addEventListener('click', this.onClick);
    RootsWishlist.subscribe(this.sync);
    this.sync();
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    // The mark sits inside the card's own link on a product card, so the click
    // must not also navigate to the product it was just saved from.
    event.preventDefault();
    event.stopPropagation();
    RootsWishlist.toggle(this.entry);
  }

  sync() {
    const saved = RootsWishlist.has(this.entry.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    this.classList.toggle('is-saved', saved);
    if (this.label) this.label.textContent = saved ? this.savedText : this.saveText;
    // The icon-only variant has no visible label, so the accessible name has to
    // carry the state instead of relying on text that is not there.
    if (!this.label) {
      this.button.setAttribute('aria-label', saved ? this.savedText : this.saveText);
    }
  }
}

if (!customElements.get('roots-save-button')) {
  customElements.define('roots-save-button', RootsSaveButton);
}

/* --- the header count ---------------------------------------------------- */

class RootsWishlistCount extends HTMLElement {
  connectedCallback() {
    this.render = this.render.bind(this);
    RootsWishlist.subscribe(this.render);
    this.render();
  }

  render() {
    const n = RootsWishlist.count();
    // Captured before the write so the first render — the one that hydrates the
    // count out of localStorage on page load — is not mistaken for a change and
    // does not animate. Only a number a shopper actually moved gets a beat.
    const changed = this.rendered && n !== this.previous;
    this.previous = n;
    this.rendered = true;

    this.hidden = n === 0;
    const digits = this.querySelector('[data-count-digits]');
    if (digits) digits.textContent = n < 100 ? String(n) : '99+';
    const spoken = this.querySelector('[data-count-spoken]');
    if (spoken) {
      // Both plural forms come from the locale file rather than being built in
      // JS, so the string stays somewhere a translator can actually reach it.
      const template = (n === 1 ? this.dataset.labelOne : this.dataset.labelOther) || '{{ count }} saved';
      spoken.textContent = template.replace('{{ count }}', n);
    }

    // `roots-motion.js` owns when the mark is applied; this only reports that
    // the number moved. Nothing breaks if that script is absent.
    if (changed && n > 0) {
      this.dispatchEvent(new CustomEvent('roots:count-changed', { bubbles: true }));
    }
  }
}

if (!customElements.get('roots-wishlist-count')) {
  customElements.define('roots-wishlist-count', RootsWishlistCount);
}
